import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ActionCell.scss';

const SERVICE_KIND = {
    NEW_CONNECTION: 'newConnection',
    RECONNECTION: 'reconnection',
    COA: 'coa',
    MCO: 'mco',
};

function resolveApplicationType(row) {
    const explicit = Number(row?.applicationType);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;

    const id = String(row?.applicationId || '').toUpperCase();
    if (id.startsWith('RC')) return 13;
    if (id.startsWith('MC')) return 5;
    if (id.startsWith('NC')) return 1;
    if (id.startsWith('CO') || id.startsWith('CA')) return 3;
    return explicit;
}

function resolveServiceKind(row, serviceKindProp) {
    if (serviceKindProp) return serviceKindProp;

    const type = resolveApplicationType(row);
    if (type === 1) return SERVICE_KIND.NEW_CONNECTION;
    if (type === 13) return SERVICE_KIND.RECONNECTION;
    if (type === 3 || type === 15) return SERVICE_KIND.COA;
    if (type === 2 || type === 5) return SERVICE_KIND.MCO;
    return null;
}

function resolveProgressNumber(row) {
    const progressNumber = Number(row?.applicationProgressNumber);
    if (Number.isFinite(progressNumber)) return progressNumber;

    const rawId = row?.applicationProgressId;
    const parsedId = Number(rawId);
    if (Number.isFinite(parsedId)) return parsedId;

    return NaN;
}

function buildMenuItems(serviceKind, row, handlers) {
    const progress = resolveProgressNumber(row);
    const items = [];
    const {
        downloadEFile,
        downloadDNPDF,
        downloadSCOPDF,
        downloadNOCPDF,
        downloadEstimatePDF,
        downloadRCOFeePDF,
        downloadFixedChargesPDF,
        downloadCP138PDF,
    } = handlers;

    const pushItem = (label, handler, ...args) => {
        if (typeof handler !== 'function') return;
        items.push({ label, onClick: () => handler(...args) });
    };

    switch (serviceKind) {
        case SERVICE_KIND.NEW_CONNECTION:
            if (progress >= 4 && progress < 7) {
                pushItem('Demand Note', downloadDNPDF, row.applicationId);
            }
            if (progress >= 7) pushItem('SCO', downloadSCOPDF, row.applicationId);
            pushItem('E-File', downloadEFile, row.applicationId);
            {
                const capitalCost = Number(
                    String(row.capitalCostAmount ?? '')
                        .replace(/,/g, '')
                        .replace(/[^\d.]/g, '')
                );
                if (capitalCost === 3100) {
                    pushItem('NOC', downloadNOCPDF, row.applicationId);
                }
            }
            if (
                progress >= 4 &&
                row.appliedLoad > 4 &&
                row.isLTLWI40Flag == 0
            ) {
                pushItem('Cost Estimate', downloadEstimatePDF, row.applicationId);
            }
            break;

        case SERVICE_KIND.RECONNECTION:
            pushItem('E-File', downloadEFile, row.applicationId);
            if (progress >= 7 && row.isDiscAboveAYear === true) {
                pushItem('Demand Note', downloadDNPDF, row.applicationId);
            }
            if (progress >= 3) {
                pushItem('RCO Fee Voucher', downloadRCOFeePDF);
                pushItem('Fixed Charges Voucher', downloadFixedChargesPDF);
            }
            if (progress === 12) pushItem('CP-138', downloadCP138PDF);
            break;

        case SERVICE_KIND.COA:
            if (progress >= 5) pushItem('Demand Note', downloadDNPDF, row.applicationId);
            pushItem('E-File', downloadEFile, row.applicationId);
            break;

        case SERVICE_KIND.MCO:
            if (
                progress >= 6 &&
                row.isMCOWithCost === true &&
                row.isMeterAvailable === true
            ) {
                pushItem('Demand Note', downloadDNPDF, row.applicationId);
            }
            if (progress >= 11) pushItem('CP-138', downloadCP138PDF, row.applicationId);
            pushItem('E-File', downloadEFile, row.applicationId);
            break;

        default:
            pushItem('E-File', downloadEFile, row.applicationId);
            if (progress >= 5) pushItem('Demand Note', downloadDNPDF, row.applicationId);
            break;
    }

    return items;
}

const ActionsCell = ({ data, theme = 'normal', serviceKind: serviceKindProp }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    const rowData = data?.row || data || {};
    const serviceKind = resolveServiceKind(rowData, serviceKindProp);
    const useOverdueColor =
        theme === 'overdue' ||
        Boolean(rowData.exceededDuration ?? data?.exceededDuration);

    const handlers = {
        downloadDNPDF: rowData.downloadDNPDF ?? data?.downloadDNPDF,
        downloadEFile: rowData.downloadEFile ?? data?.downloadEFile,
        downloadSCOPDF: rowData.downloadSCOPDF ?? data?.downloadSCOPDF,
        downloadNOCPDF: rowData.downloadNOCPDF ?? data?.downloadNOCPDF,
        downloadEstimatePDF:
            rowData.downloadEstimatePDF ?? data?.downloadEstimatePDF,
        downloadRCOFeePDF: rowData.downloadRCOFeePDF ?? data?.downloadRCOFeePDF,
        downloadFixedChargesPDF:
            rowData.downloadFixedChargesPDF ?? data?.downloadFixedChargesPDF,
        downloadCP138PDF: rowData.downloadCP138PDF ?? data?.downloadCP138PDF,
    };

    const hasDownloads = handlers.downloadDNPDF || handlers.downloadEFile;
    const menuItems = buildMenuItems(serviceKind, rowData, handlers);

    const updateMenuPosition = useCallback(() => {
        const button = buttonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const menuWidth = 120;
        const left = Math.max(
            8,
            Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)
        );

        setMenuPosition({
            top: rect.bottom + 4,
            left,
        });
    }, []);

    const onViewMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!menuOpen) updateMenuPosition();
        setMenuOpen((open) => !open);
    };

    useLayoutEffect(() => {
        if (!menuOpen) return undefined;
        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [menuOpen, updateMenuPosition]);

    useEffect(() => {
        if (!menuOpen) return undefined;

        const handlePointerDown = (e) => {
            if (
                menuRef.current?.contains(e.target) ||
                buttonRef.current?.contains(e.target)
            ) {
                return;
            }
            setMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [menuOpen]);

    const dropdown =
        menuOpen && menuItems.length > 0
            ? createPortal(
                  <div
                      ref={menuRef}
                      className="action-cell__dropdown action-cell__dropdown--portal"
                      style={{
                          position: 'fixed',
                          top: menuPosition.top,
                          left: menuPosition.left,
                          zIndex: 10000,
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                  >
                      {menuItems.map((item) => (
                          <div
                              key={item.label}
                              className="menu-item"
                              onClick={async (e) => {
                                  e.stopPropagation();
                                  setMenuOpen(false);
                                  await item.onClick();
                              }}
                          >
                              {item.label}
                          </div>
                      ))}
                  </div>,
                  document.body
              )
            : null;

    return (
        <div
            className={`action-cell${
                useOverdueColor ? ' action-cell--overdue' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
        >
            {hasDownloads && (
                <div className="action-cell__menu">
                    <button
                        ref={buttonRef}
                        type="button"
                        className="action-cell__menu-button"
                        onClick={onViewMenu}
                        onMouseDown={(e) => e.stopPropagation()}
                        aria-label="Open document menu"
                        aria-expanded={menuOpen}
                    >
                        ...
                    </button>
                    {dropdown}
                </div>
            )}
        </div>
    );
};

export default ActionsCell;
